import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import { BaseListCompDataItem, BaseListCompProps } from '@moderepo/mode-app-components';
import { EntityAttributeValueType } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { EntitiesListingTreeViewComp, EntitiesListingCardViewCompProps, EntitiesListingTableViewComp, EntitiesListingTableViewCompProps,
    EntitiesListingTreeViewCompProps } from '.';
import { EntityBasicViewUIComponentCustomSettings, EntityNode } from '../../containers';
import { ReadonlyNonEmptyArray } from '../../utils/utils';
import { EntitiesListingCardViewComp } from './EntitiesListingCardViewComp';


const useStyle = makeStyles(() => {
    return {
        root: {
            height       : '100%',
            display      : 'flex',
            flexDirection: 'column',
        },

        treeView: {
            height  : '100%',
            overflow: 'auto',
        },

        emptyMessage: {
            flex     : 1,
            alignSelf: 'center',
        },
    };
}, { name: 'EntitiesListingComp', index: 1 });



/**
 * This is the data structure for the localized attributed of the EntityNode. These are the attributes that will be displayed in the UI.
 */
export interface DisplayableEntitiesListingDataItem {
    readonly entityId?: string | undefined;
    readonly entityClass?: string | undefined;
    readonly name?: string | undefined;
    readonly description?: string | undefined;
    readonly homeId?: string | undefined;
    readonly parentId?: string | undefined;
    readonly createdAt?: string | undefined;
    readonly updatedAt?: string | undefined;
    readonly icon?: string | undefined;
    readonly image?: string | undefined;
    readonly children?: readonly EntitiesListingDataItem [] | undefined;
    readonly attributes?: {
        [key: string]: EntityAttributeValueType | undefined;
    } | undefined,
}



/**
 * This is a wrapper for an EntityNode. This component can only display the entity node's attribute value as is. It does not know
 * how to translate data or convert time strings to human readable string. The container of this component need to handle all the
 * translation and pass them to this component. Therefore, we need a wrapper for each EntityNode which contains an actual EntityNode
 * and the displayable version of the EntityNode.
 */
export interface EntitiesListingDataItem extends BaseListCompDataItem<EntityNode> {
    // The object that contains the displayable attributes of the entity node.
    readonly displayValue: DisplayableEntitiesListingDataItem;

    // A flag to specify whether this entity node item is a "Load More" item, not an actual node
    readonly isLoadMoreItem?: boolean | undefined;
}

export enum EntitiesListingViewType {
    TREE = 'Tree',
    LIST = 'List',
    GRID = 'Grid',
    TABLE = 'Table'
}

/**
 * The props required by the EntitiesListingComp components.
 */
export interface EntitiesListingCompProps extends BaseListCompProps<EntityNode> {

    readonly projectId: number;

    readonly viewType: EntitiesListingViewType;

    // The array of entities data items.
    readonly listData: ReadonlyNonEmptyArray<EntitiesListingDataItem> | undefined;

    // The entityId of the current selected entity.
    readonly selectedEntityId?: string | undefined;

    // Whether or not to display the root entity node or just start with displaying the root's children
    readonly showRootEntity?: boolean | undefined;

    readonly treeViewSettings: {
        readonly expandedNodeIds: EntitiesListingTreeViewCompProps['expandedNodeIds'];
        readonly onExpandedNodeIdsChanged: EntitiesListingTreeViewCompProps['onExpandedNodeIdsChanged']
        readonly hideExpandCollapseIcon?: boolean | undefined;
    };

    readonly tableViewSettings: {
        readonly fieldSettings: EntitiesListingTableViewCompProps['fieldsSettings'];
        readonly paginationComp?: EntitiesListingTableViewCompProps['paginationComp'] | undefined;
        readonly currentPage?: number | undefined;
        readonly itemsPerPage?: number | undefined;
    };

    readonly cardViewSettings?: {
        readonly cardCompSettings?: EntityBasicViewUIComponentCustomSettings | undefined;
    } | undefined;

    readonly texts: EntitiesListingCardViewCompProps['texts'];

    // The entity to use as current root. When in LIST or GRID view, the user can select any entity as root. This is similar to how file browsers
    // work which let the user to enter a sub directory to view the directory's contents. So if the user select a different entity as root,
    // we will show only the children of the subRootEntityId
    readonly subRootEntityId?: string | undefined;

    // The handler for when the user select another entity as root
    readonly onChangeSubRootEntity?: ((entityNode: EntityNode)=> void) | undefined;
}


/**
 * This is a component that display list of entities.
 */
export const EntitiesListingComp: React.FC<EntitiesListingCompProps> = (props: EntitiesListingCompProps) => {
    const classes = useStyle();

    const hasEntityNodes = useMemo(() => {
        return props.listData && props.listData.length > 0 && props.listData[0].actualValue.children.length > 0;
    }, [props.listData]);


    return (
        <div className={clsx(classes.root, props.className)}>
            {props.listData && hasEntityNodes && (
                <>
                    {props.viewType === EntitiesListingViewType.TREE && (
                        <EntitiesListingTreeViewComp
                            className={classes.treeView}
                            listData={props.listData}
                            expandedNodeIds={props.treeViewSettings.expandedNodeIds}
                            onExpandedNodeIdsChanged={props.treeViewSettings.onExpandedNodeIdsChanged}
                            hideExpandCollapseIcon={props.treeViewSettings.hideExpandCollapseIcon}
                            selectedEntityId={props.selectedEntityId}
                            onEntitySelected={props.onListItemSelected}
                            onPreviewItemClicked={props.onPreviewItemClicked}
                        />
                    )}
                    {(props.viewType === EntitiesListingViewType.LIST || props.viewType === EntitiesListingViewType.GRID) && (
                        <EntitiesListingCardViewComp
                            projectId={props.projectId}
                            listData={props.listData}
                            selectedEntityId={props.selectedEntityId}
                            viewType={props.viewType}
                            cardViewSettings={props.cardViewSettings}
                            texts={props.texts}
                            onEntitySelected={props.onListItemSelected}
                            onPreviewItemClicked={props.onPreviewItemClicked}
                            subRootEntityId={props.subRootEntityId}
                            noDataMessageComp={props.noDataMessageComp}
                            onChangeRootEntity={props.onChangeSubRootEntity}
                        />
                    )}
                    {(props.viewType === EntitiesListingViewType.TABLE) && (
                        <EntitiesListingTableViewComp
                            projectId={props.projectId}
                            listData={props.listData}
                            selectedEntityId={props.selectedEntityId}
                            fieldsSettings={props.tableViewSettings.fieldSettings}
                            paginationComp={props.tableViewSettings.paginationComp}
                            currentPage={props.tableViewSettings.currentPage}
                            itemsPerPage={props.tableViewSettings.itemsPerPage}
                            texts={props.texts}
                            onEntitySelected={props.onListItemSelected}
                            onPreviewItemClicked={props.onPreviewItemClicked}
                            subRootEntityId={props.subRootEntityId}
                            emptyDataMessageComp={props.emptyDataMessageComp}
                            noDataMessageComp={props.noDataMessageComp}
                            onChangeRootEntity={props.onChangeSubRootEntity}
                        />
                    )}
                </>
            )}

            {props.listData && !hasEntityNodes && (
                <div className={classes.emptyMessage}>
                    {props.noDataMessageComp}
                </div>
            )}

            {!props.listData && (
                <Box padding={2}>
                    <CircularProgress />
                </Box>
            )}
        </div>
    );
};
